import React, { useCallback, useEffect, useRef } from "react";

import { usePrompt } from "hooks";
import { useBulkExtractionSocketHook } from "hooks/socket-hooks/use-bulk-extraction-socket-hook";
import { useAccountSelector, useBulkBatchesSelector, useBulkUploadActions } from "store";

import { BulkUploadWidget } from "./BulkUploadWidget";

export const BulkUploadController: React.FC = () => {
  const { enable_bulk_upload_review } = useAccountSelector() || {};
  const {
    getBulkUploadBatchesAction,
    setBulkBatchesAction,
    applyBulkFileStatusAction,
    markBulkBatchAwaitingReviewAction,
    removeBulkBatchAction,
    clearBulkUploadAction
  } = useBulkUploadActions();
  const openPrompt = usePrompt();
  const batches = useBulkBatchesSelector();

  const refetchSnapshot = useCallback(async () => {
    const data = await getBulkUploadBatchesAction();
    setBulkBatchesAction(data);
  }, [getBulkUploadBatchesAction, setBulkBatchesAction]);

  useEffect(() => {
    if (!enable_bulk_upload_review) {
      clearBulkUploadAction();
      return;
    }
    refetchSnapshot();
  }, [enable_bulk_upload_review, refetchSnapshot, clearBulkUploadAction]);

  useBulkExtractionSocketHook({
    onFileStatus: applyBulkFileStatusAction,
    onBatchAwaitingReview: ({ uploadBatchId }) => markBulkBatchAwaitingReviewAction(uploadBatchId),
    onBatchDismissed: ({ uploadBatchId }) => removeBulkBatchAction(uploadBatchId)
  });

  // Open the progress widget whenever a batch the user hasn't dismissed this
  // session shows up. Closing the panel marks the batches present at that moment
  // as "seen" (so it doesn't immediately reopen), but a brand-new upload — a new
  // batch id — reopens it. Note: closing the panel does NOT remove batches from
  // the store, so a plain `hasBatches` latch would stay stuck and never reopen.
  const widgetOpenRef = useRef(false);
  const seenBatchIdsRef = useRef<Set<string>>(new Set());

  // Keep the latest batch map reachable inside the close callback below without
  // re-subscribing the effect (avoids a stale closure on `batches`).
  const batchesRef = useRef(batches);
  batchesRef.current = batches;

  // Forget ids that left the store so the "seen" set can't grow unbounded.
  useEffect(() => {
    seenBatchIdsRef.current.forEach((id) => {
      if (!batches[id]) seenBatchIdsRef.current.delete(id);
    });
  }, [batches]);

  const hasUnseenBatch = Object.keys(batches).some((id) => !seenBatchIdsRef.current.has(id));

  useEffect(() => {
    if (!enable_bulk_upload_review || widgetOpenRef.current || !hasUnseenBatch) return;

    widgetOpenRef.current = true;
    openPrompt({
      componentType: "status",
      statusTitle: "Uploading files",
      Component: BulkUploadWidget
    }).finally(() => {
      widgetOpenRef.current = false;
      Object.keys(batchesRef.current).forEach((id) => seenBatchIdsRef.current.add(id));
    });
  }, [enable_bulk_upload_review, hasUnseenBatch, openPrompt]);

  return null;
};
